import { useEffect, useState } from 'react';
import { Banner, Modal, Select, Tag, Typography } from '@douyinfe/semi-ui';
import { api } from './api';
import { integrationError, validateTokenEntrance, type AppBossIntegration } from './appBossIntegration';
import type { BaseURL } from './types';
import './appBossIntegrations.css';

const { Text } = Typography;

export function AppBossEntranceSettings({ draft, existing, onChange, disabled, onDenied, integrationLocked = false }: {
  draft: Partial<BaseURL>; existing?: BaseURL | null; onChange: (value: Partial<BaseURL>) => void; disabled: boolean; onDenied: () => void; integrationLocked?: boolean;
}) {
  const [items, setItems] = useState<AppBossIntegration[]>([]), [loading, setLoading] = useState(true), [error, setError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    void api.appBossIntegrations(controller.signal).then((result) => {
      if (!controller.signal.aborted) { setItems(result.integrations); setLoading(false); }
    }).catch((error) => {
      if (controller.signal.aborted) return;
      setError(integrationError(error)); setLoading(false);
      if ([401, 403].includes(error?.status)) onDenied();
    });
    return () => controller.abort();
  }, []);
  const enabled = items.filter((item) => item.status === 'enabled');
  const current = enabled.find((item) => item.id === draft.appbossIntegrationId);
  const problem = validateTokenEntrance(draft, existing);
  return <div className="integration-token-settings">
    {!existing?.appbossDualAuth && <Banner type="info" closeIcon={null} description="保存设置不会启用入口。保存后可在“启用与停用”中检查状态；付费调用还需要 AppBoss 应用授权与计费设置。" />}
    <div><Text>入口状态：</Text><Tag color={draft.status === 'enabled' ? 'green' : 'grey'}>{draft.status === 'enabled' ? '已启用' : '未启用'}</Tag></div>
    {error && <Banner type="danger" closeIcon={null} description={error} />}
    <label className="integration-field"><span id="entrance-integration-label">AppBoss 连接</span><Select aria-labelledby="entrance-integration-label" value={draft.appbossIntegrationId || undefined} loading={loading} disabled={disabled || integrationLocked || loading || !!error} filter placeholder="选择已连接的 AppBoss" onChange={(value) => { if (!integrationLocked) onChange({ ...draft, appbossIntegrationId: String(value) }); }} optionList={(integrationLocked ? items : enabled).map((item) => ({ value: item.id, label: item.name }))} /></label>
    {integrationLocked && !existing?.appbossDualAuth && <Text type="secondary">此入口的认证方式和 AppBoss 连接不可更换。</Text>}
    {!loading && !error && !current && <Text type="warning">当前 AppBoss 连接未启用或尚未选择。请在“AppBoss 连接”中检查连接。</Text>}
    {problem && <Banner type="warning" closeIcon={null} description={problem} />}
  </div>;
}

export function AppBossTokenDocsModal({ base, visible, onClose }: { base: BaseURL; visible: boolean; onClose: () => void }) {
  return <Modal title="AppBoss 令牌接入状态" visible={visible} onCancel={onClose} closeOnEsc footer={null} width="min(760px, calc(100vw - 24px))">
    <div className="integration-token-docs">
      <dl><dt>入口</dt><dd><code>{base.name} ({base.id})</code></dd><dt>AppBoss 连接</dt><dd><code>{base.appbossIntegrationId || '未绑定'}</code></dd></dl>
      <Text>{base.appbossDualAuth ? '同一个入口地址同时接受固定 API Key 和 AppBoss 动态 Token，两者均放在 Authorization: Bearer 中。' : '该入口使用 AppBoss 签发的短期专用 Token。'}使用动态 Token 时不发送 X-APPBOSS-UID，用户身份来自验证后的 Token。</Text>
      <Text type="secondary">应用专属的令牌兑换与有界付费请求示例，请使用 AppBoss 对应应用的 AiGate 接入说明。本页不提供绕过用户授权的管理员付费测试。</Text>
    </div>
  </Modal>;
}
